
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { API_CONFIG } from "@/constants/env";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  FileText, 
  LightbulbIcon, 
  BarChart3, 
  GitCompareArrows,
  Download,
  LucideIcon,
  Component,
  Timer,
  Check
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { cn } from "@/lib/utils";

interface ComplianceAIResponseProps {
  response: {
    text?: string;
    text_html?: string;
    advice?: string;
    advice_html?: string;
    diagram?: any; // Changed to any to handle both string and object
    content?: string;
    metadata?: any;
  };
  isLoading: boolean;
  error: Error | null;
  onReset: () => void;
}

// Tab interface
interface TabDefinition {
  id: string;
  label: string;
  icon: LucideIcon;
}

// Helper function to format multi-paragraph content
const formatContent = (content: string): string[] => {
  // Handle different paragraph separators
  if (content.includes('\n\n')) {
    return content.split('\n\n').filter(p => p.trim() !== '');
  } else if (content.includes('\n')) {
    return content.split('\n').filter(p => p.trim() !== '');
  } else if (content.includes('. ')) {
    // If no clear paragraph breaks, try to break by sentences
    const sentences = content.split('. ');
    const paragraphs = [];
    let currentParagraph = '';
    
    sentences.forEach((sentence, i) => {
      // Add period back except for the last item if it doesn't have one
      const formattedSentence = i < sentences.length - 1 || sentences[sentences.length - 1].endsWith('.') 
        ? `${sentence}.` 
        : sentence;
        
      if ((currentParagraph + formattedSentence).length > 150) {
        if (currentParagraph) {
          paragraphs.push(currentParagraph);
        }
        currentParagraph = formattedSentence;
      } else {
        currentParagraph += (currentParagraph ? ' ' : '') + formattedSentence;
      }
    });
    
    if (currentParagraph) {
      paragraphs.push(currentParagraph);
    }
    
    return paragraphs;
  }
  
  // Fallback to returning the whole content as one paragraph
  return [content];
};

// Helper function to format analysis content with structure (titles, lists, etc.)
const formatStructuredAnalysis = (content: string): JSX.Element => {
  if (!content) return <p>No analysis was provided.</p>;

  // Split content into lines to process
  const lines = content.split('\n');
  const elements: JSX.Element[] = [];
  let listItems: string[] = [];
  let inList = false;
  
  lines.forEach((line, index) => {
    const trimmedLine = line.trim();
    
    // Skip empty lines
    if (!trimmedLine) return;
    
    // Check for section titles (various patterns)
    if (
      // Explicit markdown-style headers
      trimmedLine.startsWith('# ') || 
      trimmedLine.startsWith('## ') ||
      // Title patterns with colons
      /^[A-Z][A-Za-z\s]{2,30}:/.test(trimmedLine) ||
      // All caps titles
      /^[A-Z\s]{5,30}$/.test(trimmedLine) ||
      // Numbered section titles
      /^\d+\.\s+[A-Z][A-Za-z\s]{2,50}$/.test(trimmedLine)
    ) {
      // If we were in a list, add it before the new section
      if (inList && listItems.length > 0) {
        elements.push(
          <ul key={`list-${index}`} className="list-disc pl-6 space-y-1 mb-4">
            {listItems.map((item, i) => <li key={i}>{item}</li>)}
          </ul>
        );
        listItems = [];
        inList = false;
      }
      
      // Format the title and add it
      let title = trimmedLine;
      // Remove markdown symbols if present
      title = title.replace(/^#+\s+/, '');
      
      elements.push(
        <h3 key={`title-${index}`} className="text-lg font-semibold mt-6 mb-3 text-purple-700 dark:text-purple-400">
          {title}
        </h3>
      );
    }
    // Check for list items
    else if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ') || /^\d+\.\s+/.test(trimmedLine)) {
      inList = true;
      // Remove the bullet/number and add to list items
      const itemText = trimmedLine.replace(/^[-*]\s+/, '').replace(/^\d+\.\s+/, '');
      listItems.push(itemText);
    }
    // End of list - add the accumulated list items
    else if (inList && listItems.length > 0) {
      elements.push(
        <ul key={`list-${index}`} className="list-disc pl-6 space-y-1 mb-4">
          {listItems.map((item, i) => <li key={i}>{item}</li>)}
        </ul>
      );
      listItems = [];
      inList = false;
      
      // Add the current line as a paragraph
      elements.push(<p key={`p-${index}`} className="mb-4">{trimmedLine}</p>);
    }
    // Regular paragraph
    else {
      elements.push(<p key={`p-${index}`} className="mb-4">{trimmedLine}</p>);
    }
  });
  
  // Add any remaining list items
  if (inList && listItems.length > 0) {
    elements.push(
      <ul key="list-final" className="list-disc pl-6 space-y-1 mb-4">
        {listItems.map((item, i) => <li key={i}>{item}</li>)}
      </ul>
    );
  }
  
  // If no structured elements were found, fall back to simple paragraphs
  if (elements.length === 0) {
    return (
      <div>
        {formatContent(content).map((paragraph, i) => (
          <p key={i} className="mb-4">{paragraph}</p>
        ))}
      </div>
    );
  }
  
  return <div>{elements}</div>;
};

// Helper function to format recommendations into structured JSX
const formatRecommendations = (advice: string): JSX.Element[] => {
  // Look for numbered recommendations pattern (1. 2. 3. etc)
  const hasNumberedItems = /\d+\.\s/.test(advice);
  
  if (hasNumberedItems) {
    const items: JSX.Element[] = [];
    let intro = '';
    let inRecommendation = false;
    let currentRecommendation = '';
    let currentDetails: string[] = [];
    let recommendationNumber = 0;
    
    // Split by lines to process recommendations and their details
    const lines = advice.split('\n');
    
    lines.forEach((line, i) => {
      const trimmedLine = line.trim();
      
      // Check if this is a numbered recommendation
      const recommendationMatch = trimmedLine.match(/^(\d+)\.\s+(.*)/);
      
      if (recommendationMatch) {
        // If we were already processing a recommendation, add it to items
        if (inRecommendation) {
          items.push(
            <div key={`rec-${recommendationNumber}`} className="mb-6">
              <h3 className="text-lg font-medium mb-2">
                {recommendationNumber}. {currentRecommendation}
              </h3>
              <ul className="list-disc pl-6 space-y-1">
                {currentDetails.map((detail, j) => (
                  <li key={j}>{detail}</li>
                ))}
              </ul>
            </div>
          );
        } else if (i > 0) {
          // If this is the first recommendation but not the first line, 
          // previous content is the intro
          intro = lines.slice(0, i).join(' ').trim();
        }
        
        // Start processing the new recommendation
        inRecommendation = true;
        recommendationNumber = parseInt(recommendationMatch[1]);
        currentRecommendation = recommendationMatch[2];
        currentDetails = [];
      } 
      // Check if this is a detail bullet point for the current recommendation
      else if (inRecommendation && trimmedLine.startsWith('-')) {
        currentDetails.push(trimmedLine.substring(1).trim());
      }
      // Continuation of recommendation or other content
      else if (inRecommendation && trimmedLine && !trimmedLine.startsWith('-')) {
        // If this looks like a continuation of the recommendation title
        if (currentDetails.length === 0) {
          currentRecommendation += ' ' + trimmedLine;
        } 
        // If we're between bullet points, it might be another paragraph of the previous bullet
        else if (currentDetails.length > 0 && !trimmedLine.startsWith('-')) {
          const lastDetailIndex = currentDetails.length - 1;
          currentDetails[lastDetailIndex] += ' ' + trimmedLine;
        }
      }
      // Content after all recommendations
      else if (!inRecommendation && i > 0) {
        // This will be handled after the loop for the conclusion
      }
    });
    
    // Add the last recommendation if there is one
    if (inRecommendation) {
      items.push(
        <div key={`rec-${recommendationNumber}`} className="mb-6">
          <h3 className="text-lg font-medium mb-2">
            {recommendationNumber}. {currentRecommendation}
          </h3>
          <ul className="list-disc pl-6 space-y-1">
            {currentDetails.map((detail, j) => (
              <li key={j}>{detail}</li>
            ))}
          </ul>
        </div>
      );
    }
    
    // Find any conclusion text (after the last numbered item)
    // Replace findLastIndex with a compatible alternative
    let lastNumberedItemIndex = -1;
    for (let i = lines.length - 1; i >= 0; i--) {
      if (/^\d+\.\s+/.test(lines[i].trim())) {
        lastNumberedItemIndex = i;
        break;
      }
    }
    
    if (lastNumberedItemIndex > -1 && lastNumberedItemIndex < lines.length - 1) {
      const conclusionLines = lines.slice(lastNumberedItemIndex + 1)
        .filter(line => !line.trim().startsWith('-') && line.trim() !== '');
      
      if (conclusionLines.length > 0) {
        const conclusion = conclusionLines.join(' ').trim();
        if (conclusion) {
          items.push(
            <p key="conclusion" className="mt-4 font-medium">
              {conclusion}
            </p>
          );
        }
      }
    }
    
    // Add intro text if it exists
    const result: JSX.Element[] = [];
    if (intro) {
      result.push(
        <p key="intro" className="mb-4">
          {intro}
        </p>
      );
    }
    
    return [...result, ...items];
  } else {
    // Fallback for when the recommendations are not structured with numbers
    return formatContent(advice).map((paragraph, i) => (
      <p key={i} className="mb-4">{paragraph}</p>
    ));
  }
};

// Component to display the process comparison
const ProcessComparison = ({ diagram }: { diagram: any }) => {
  if (!diagram) {
    return <p>No comparison data available.</p>;
  }

  try {
    // Handle when diagram is an object with arrays
    if (typeof diagram === 'object' && diagram !== null) {
      const currentProcess = Array.isArray(diagram.current_process) ? diagram.current_process : ['No current process data'];
      const aiProcess = Array.isArray(diagram.ai_process) ? diagram.ai_process : ['No AI process data'];
      
      // Extract benefits data from the enhanced diagram format, or use defaults
      const benefits = diagram.benefits || {};
      const timeReduction = {
        before: benefits.before_duration || "Weeks",
        after: benefits.after_duration || "Days",
        savings: benefits.time_reduction || "~70%"
      };
      
      // Extract implementation timeline and metrics if available
      const implementationTimeline = diagram.implementation_timeline || [];
      const keyMetrics = diagram.key_metrics || [];
      
      return (
        <div className="py-6">
          {/* Key benefits section - Moved to the top */}
          <div className="mb-8 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg shadow-sm">
              <div className="flex items-center mb-2">
                <svg className="w-5 h-5 text-purple-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h4 className="font-medium">Time Savings</h4>
              </div>
              <p className="text-sm">{benefits.time_reduction || "Significant reduction"} in process time</p>
            </div>
            
            <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg shadow-sm">
              <div className="flex items-center mb-2">
                <svg className="w-5 h-5 text-purple-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h4 className="font-medium">Accuracy</h4>
              </div>
              <p className="text-sm">{benefits.accuracy_improvement || "Enhanced"} compliance accuracy</p>
            </div>
            
            <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg shadow-sm">
              <div className="flex items-center mb-2">
                <svg className="w-5 h-5 text-amber-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0l-8 8-4-4-6 6" />
                </svg>
                <h4 className="font-medium">Cost Efficiency</h4>
              </div>
              <p className="text-sm">{benefits.cost_savings || "Substantial"} cost savings</p>
            </div>
            
            <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg shadow-sm">
              <div className="flex items-center mb-2">
                <svg className="w-5 h-5 text-indigo-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <h4 className="font-medium">Risk Reduction</h4>
              </div>
              <p className="text-sm">{benefits.risk_reduction || "Significant"} risk reduction</p>
            </div>
          </div>

          {/* Enhanced visualization with flow diagram */}
          <div className="flex flex-col md:flex-row gap-8 mb-6">
            {/* Current Process Flow */}
            <div className="flex-1 bg-gray-100 dark:bg-gray-800 p-6 rounded-lg shadow-md">
              <h3 className="text-lg font-medium mb-6 text-center">Current Process</h3>
              <div className="flex flex-col items-center">
                {currentProcess.map((step: string, index: number) => (
                  <div key={index} className="relative w-full max-w-[280px]">
                    <div className="bg-gray-200 dark:bg-gray-700 p-4 rounded-md border border-gray-300 dark:border-gray-600 mb-2">
                      <h4 className="font-medium text-center">{step}</h4>
                    </div>
                    {index < currentProcess.length - 1 && (
                      <div className="flex justify-center my-2">
                        <svg className="w-6 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                        </svg>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-6 text-center text-sm text-gray-500">
                <span className="inline-block bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 px-3 py-1 rounded">
                  Time to completion: {timeReduction.before}
                </span>
              </div>
            </div>
            
            {/* Process comparison arrow */}
            <div className="hidden md:flex items-center justify-center">
              <div className="relative">
                <svg className="w-12 h-12 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 -translate-y-full whitespace-nowrap bg-purple-100 text-purple-700 text-xs px-2 py-1 rounded">
                  {timeReduction.savings} faster
                </div>
              </div>
            </div>
            
            {/* Mobile arrow (only visible on small screens) */}
            <div className="flex md:hidden justify-center my-4">
              <div className="relative">
                <svg className="w-12 h-12 text-purple-500 transform rotate-90" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
                <div className="absolute -left-4 top-1/2 -translate-y-1/2 whitespace-nowrap bg-purple-100 text-purple-700 text-xs px-2 py-1 rounded">
                  {timeReduction.savings} faster
                </div>
              </div>
            </div>
            
            {/* AI-Optimized Process Flow */}
            <div className="flex-1 bg-purple-50 dark:bg-purple-900/30 p-6 rounded-lg shadow-md">
              <h3 className="text-lg font-medium mb-6 text-center">AI-Optimized Process</h3>
              <div className="flex flex-col items-center">
                {aiProcess.map((step: string, index: number) => (
                  <div key={index} className="relative w-full max-w-[280px]">
                    <div className="bg-purple-100 dark:bg-purple-800/50 p-4 rounded-md border border-purple-200 dark:border-purple-700 mb-2">
                      <h4 className="font-medium text-center">{step}</h4>
                      <div className="mt-2 text-xs text-gray-600 dark:text-gray-400 text-center">
                        <span className="inline-block bg-purple-200 dark:bg-purple-700 px-2 py-0.5 rounded">
                          {index === 0 ? "AI-powered" : 
                           index === 1 ? "Automated" : 
                           index === 2 ? "Real-time" : "Smart"}
                        </span>
                      </div>
                    </div>
                    {index < aiProcess.length - 1 && (
                      <div className="flex justify-center my-2">
                        <svg className="w-6 h-8 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                        </svg>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-6 text-center text-sm text-gray-500">
                <span className="inline-block bg-purple-200 text-purple-700 dark:bg-purple-800/50 dark:text-purple-300 px-3 py-1 rounded">
                  Time to completion: {timeReduction.after}
                </span>
              </div>
            </div>
          </div>
          
          {/* Implementation timeline section */}
          {implementationTimeline.length > 0 && (
            <div className="mt-8">
              <h3 className="text-xl font-medium mb-4">Implementation Roadmap</h3>
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-6">
                <div className="flex flex-col md:flex-row items-start gap-6">
                  {implementationTimeline.map((phase, index) => (
                    <div key={index} className="flex-1 relative">
                      <div className="flex items-center mb-3">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 mr-2">
                          {index + 1}
                        </div>
                        <h4 className="font-medium">{phase.split(':')[0]}</h4>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{phase.split(':')[1] || phase}</p>
                      {index < implementationTimeline.length - 1 && (
                        <div className="hidden md:block absolute top-4 -right-3 w-6 h-1 bg-purple-200 dark:bg-purple-800"></div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          {/* Key metrics section */}
          {keyMetrics.length > 0 && (
            <div className="mt-8">
              <h3 className="text-xl font-medium mb-4">Key Performance Indicators</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {keyMetrics.map((metric, index) => (
                  <div key={index} className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center mb-2">
                      <div className="w-2 h-8 bg-gradient-to-b from-purple-400 to-indigo-500 rounded-full mr-3"></div>
                      <span className="text-sm font-medium">{metric}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }

    // Fallback for unexpected formats
    return (
      <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
        <h3 className="text-lg font-medium mb-3">Process Comparison Data</h3>
        <pre className="whitespace-pre-wrap text-sm">{JSON.stringify(diagram, null, 2)}</pre>
      </div>
    );
  } catch (err) {
    console.error("Error rendering diagram:", err);
    if (err instanceof Error) {
      console.log('Diagram error details:', {
        name: err.name,
        message: err.message,
        stack: err.stack
      });
    }
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
        <h3 className="text-lg font-medium mb-3 text-red-700 dark:text-red-300">Error Rendering Diagram</h3>
        <p>{err instanceof Error ? err.message : 'Unknown error occurred'}</p>
      </div>
    );
  }
};

const ComplianceAIResponse = ({ response, onReset, isLoading, error }: ComplianceAIResponseProps) => {
  const [activeTab, setActiveTab] = useState<string>("analysis");
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  
  // Define tabs
  const tabs: TabDefinition[] = [
    { id: "analysis", label: "Current State Analysis", icon: FileText },
    { id: "recommendations", label: "AI Recommendations", icon: LightbulbIcon },
    { id: "comparison", label: "Process Comparison", icon: GitCompareArrows },
    { id: "timeline", label: "Implementation Plan", icon: Timer },
  ];
  
  const handlePdfRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      setSubmitError("Please enter a valid email address");
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError("");
    
    try {
      const result = await fetch(`${API_CONFIG.API_BASE_URL}/compliance-ai/pdf/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          ai_response: response
        }),
      });
      
      if (!result.ok) {
        throw new Error('Failed to generate PDF');
      }
      
      setShowEmailDialog(false);
      setEmail("");
      setShowSuccessDialog(true);
    } catch (error) {
      console.error('Error requesting PDF:', error);
      if (error instanceof Error) {
        console.log('PDF request error details:', {
          name: error.name,
          message: error.message,
          stack: error.stack
        });
      }
      setSubmitError(error instanceof Error ? error.message : 'An unknown error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const { text, text_html, advice, advice_html, diagram } = response;

  // Extract implementation timeline for dedicated tab
  const implementationTimeline = diagram && diagram.implementation_timeline ? diagram.implementation_timeline : [];

  // Render analysis tab content
  const renderAnalysisTab = () => (
    <div className="p-6 space-y-6">
      <h2 className="text-xl font-bold pb-2 border-b flex items-center gap-2">
        <FileText className="h-5 w-5 text-purple-500" />
        Current State Analysis
      </h2>
      
      {/* Key Statistics Cards - Moved to the top */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Process Efficiency</p>
              <h4 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {diagram?.benefits?.time_reduction || "65%"}
              </h4>
            </div>
            <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-full">
              <Timer className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Potential time savings</p>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Quality Improvement</p>
              <h4 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {diagram?.benefits?.accuracy_improvement || "75%"}
              </h4>
            </div>
            <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-full">
              <Component className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Compliance accuracy gain</p>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Cost Savings</p>
              <h4 className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                {diagram?.benefits?.cost_savings || "40%"}
              </h4>
            </div>
            <div className="p-2 bg-amber-100 dark:bg-amber-900/20 rounded-full">
              <BarChart3 className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Operational cost reduction</p>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Risk Reduction</p>
              <h4 className="text-2xl font-bold text-green-600 dark:text-green-400">
                {diagram?.benefits?.risk_reduction || "60%"}
              </h4>
            </div>
            <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-full">
              <svg className="h-5 w-5 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Compliance risk mitigation</p>
        </div>
      </div>
      
      {/* Analysis Content Card - Now below the metric cards */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-5">
        <h3 className="text-lg font-semibold mb-4">Comprehensive Current State Analysis</h3>
        <div className="prose dark:prose-invert max-w-none">
          {text_html ? (
            <div dangerouslySetInnerHTML={{ __html: text_html }} />
          ) : text && typeof text === 'string' ? (
            formatStructuredAnalysis(text)
          ) : (
            <p>No analysis was provided.</p>
          )}
        </div>
      </div>
    </div>
  );

  // Render recommendations tab content
  const renderRecommendationsTab = () => (
    <div className="p-6 space-y-6">
      <h2 className="text-xl font-bold pb-2 border-b flex items-center gap-2">
        <LightbulbIcon className="h-5 w-5 text-purple-500" />
        AI-Powered Recommendations
      </h2>
      
      <div className="space-y-4">
        {advice_html ? (
          <div className="prose dark:prose-invert max-w-none bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-5">
            <div dangerouslySetInnerHTML={{ __html: advice_html }} />
          </div>
        ) : advice && typeof advice === 'string' ? (
          <Accordion type="multiple" className="space-y-4">
            {/* Parse the advice to find main recommendations */}
            {findRecommendationSections(advice).map((section, index) => (
              <AccordionItem 
                key={index} 
                value={`recommendation-${index}`}
                className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
              >
                <AccordionTrigger className="px-5 py-4 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/80">
                  <div className="flex flex-col items-start text-left">
                    <h3 className="text-lg font-semibold">{section.title}</h3>
                    {section.subtitle && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{section.subtitle}</p>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-5 py-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                  <div className="prose dark:prose-invert max-w-none">
                    {section.content}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        ) : (
          <p>No specific recommendations provided.</p>
        )}
      </div>
    </div>
  );

  // Render comparison tab content
  const renderComparisonTab = () => (
    <div className="p-6 space-y-6">
      <h2 className="text-xl font-bold pb-2 border-b flex items-center gap-2">
        <GitCompareArrows className="h-5 w-5 text-purple-500" />
        Process Comparison
      </h2>
      
      <div className="w-full overflow-auto">
        <ProcessComparison diagram={diagram} />
      </div>
    </div>
  );

  // Render timeline tab content
  const renderTimelineTab = () => (
    <div className="p-6 space-y-6">
      <h2 className="text-xl font-bold pb-2 border-b flex items-center gap-2">
        <Timer className="h-5 w-5 text-purple-500" />
        Implementation Plan
      </h2>
      
      {implementationTimeline.length > 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-5">
          <div className="space-y-8">
            {implementationTimeline.map((phase, index) => (
              <div key={index} className="relative pl-10 pb-8 border-l-2 border-purple-300 dark:border-purple-700 last:border-0">
                {/* Phase dot */}
                <div className="absolute left-[-9px] top-0 h-5 w-5 rounded-full bg-purple-500 dark:bg-purple-600" />
                
                {/* Phase content */}
                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-md p-4 shadow-sm border border-purple-100 dark:border-purple-800">
                  <div className="flex items-center mb-3">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-800 text-purple-700 dark:text-purple-300 mr-2">
                      {index + 1}
                    </div>
                    <h4 className="font-medium text-lg">{phase.split(':')[0]}</h4>
                  </div>
                  <p className="text-gray-600 dark:text-gray-300">{phase.split(':')[1] || phase}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
          <Timer className="h-12 w-12 mx-auto text-gray-400 mb-3" />
          <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-1">No Implementation Timeline</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">No implementation timeline was provided in the response.</p>
        </div>
      )}
      
      {/* Key metrics section */}
      {diagram?.key_metrics && diagram.key_metrics.length > 0 && (
        <div className="mt-8">
          <h3 className="text-xl font-medium mb-4">Key Performance Indicators</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {diagram.key_metrics.map((metric, index) => (
              <div key={index} className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="flex items-center mb-2">
                  <div className="w-2 h-8 bg-gradient-to-b from-purple-400 to-indigo-500 rounded-full mr-3"></div>
                  <span className="text-sm font-medium">{metric}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // Helper function to parse recommendation sections for the accordion
  function findRecommendationSections(text: string) {
    const sections = [];
    let currentSection = { title: '', subtitle: '', content: null as JSX.Element | null };
    
    // Try to find section headers (## Something)
    const lines = text.split('\n');
    let inSection = false;
    let sectionLines: string[] = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // Check if this is a main section header (## Something)
      if (line.match(/^##\s+.+$/)) {
        // If we were already in a section, add it to our results
        if (inSection && currentSection.title && sectionLines.length > 0) {
          sections.push({
            ...currentSection,
            content: formatRecommendationContent(sectionLines)
          });
        }
        
        // Start a new section
        const title = line.replace(/^##\s+/, '');
        currentSection = { 
          title: title, 
          subtitle: '', 
          content: null 
        };
        inSection = true;
        sectionLines = [];
        
        // Try to find a subtitle (the next non-blank line if it doesn't start with #)
        for (let j = i + 1; j < lines.length; j++) {
          const nextLine = lines[j].trim();
          if (nextLine && !nextLine.startsWith('#') && !nextLine.startsWith('-')) {
            currentSection.subtitle = nextLine.replace(/\*\*/g, '');
            break;
          }
          if (nextLine.startsWith('#')) break;
        }
      }
      // If we're in a section, collect its content
      else if (inSection) {
        sectionLines.push(line);
      }
    }
    
    // Add the last section if there is one
    if (inSection && currentSection.title && sectionLines.length > 0) {
      sections.push({
        ...currentSection,
        content: formatRecommendationContent(sectionLines)
      });
    }
    
    // If no sections were found with ## headers, try to use numbered sections instead
    if (sections.length === 0) {
      return [{
        title: "AI Recommendations",
        subtitle: "Detailed optimization suggestions",
        content: <div>{formatRecommendations(text)}</div>
      }];
    }
    
    return sections;
  }
  
  // Format the content of each recommendation section
  function formatRecommendationContent(lines: string[]) {
    const content = lines.join('\n');
    return <div>{formatRecommendations(content)}</div>;
  }

  // Render the active tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case "analysis":
        return renderAnalysisTab();
      case "recommendations":
        return renderRecommendationsTab();
      case "comparison":
        return renderComparisonTab();
      case "timeline":
        return renderTimelineTab();
      default:
        return renderAnalysisTab();
    }
  };

  return (
    <div className="space-y-6">
      {/* Action Buttons - At the top */}
      <div className="flex justify-center gap-4 mb-2">
        <Button onClick={onReset} variant="outline" size="lg">
          Start Over
        </Button>
        <Button 
          onClick={() => setShowEmailDialog(true)} 
          variant="default" 
          size="lg" 
          className="bg-purple-600 hover:bg-purple-500"
        >
          <Download className="mr-2 h-4 w-4" /> Download as PDF
        </Button>
      </div>
      
      <Card className="border-border/40 bg-background/60  overflow-hidden border-t-4 border-b-4 border-t-purple-500 border-b-purple-500">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="border-b border-border/50">
            <TabsList className="w-full flex justify-between rounded-none bg-transparent border-b p-0">
              {tabs.map((tab) => (
                <TabsTrigger 
                  key={tab.id} 
                  value={tab.id}
                  className={cn(
                    "flex-1 py-4 rounded-none data-[state=active]:border-b-2 data-[state=active]:border-purple-500 data-[state=active]:shadow-none data-[state=active]:text-purple-700 dark:data-[state=active]:text-purple-400",
                    "bg-transparent hover:bg-gray-50 dark:hover:bg-gray-800/50 gap-2 font-medium",
                    "border border-gray-200 dark:border-gray-700 border-b-0 mx-0.5 first:ml-0 last:mr-0",
                    "shadow-sm"
                  )}
                >
                  <tab.icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          <TabsContent value={activeTab} className="mt-0">
            {/* Render tab content based on activeTab */}
            {activeTab === "analysis" && (
              <div className="p-6 space-y-6">
                <h2 className="text-xl font-bold pb-2 border-b flex items-center gap-2">
                  <FileText className="h-5 w-5 text-purple-500" />
                  Current State Analysis
                </h2>
                
                {/* Key Statistics Cards - Moved to the top */}
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
                  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Process Efficiency</p>
                        <h4 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                          {diagram?.benefits?.time_reduction || "65%"}
                        </h4>
                      </div>
                      <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-full">
                        <Timer className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Potential time savings</p>
                  </div>
                  
                  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Quality Improvement</p>
                        <h4 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                          {diagram?.benefits?.accuracy_improvement || "75%"}
                        </h4>
                      </div>
                      <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-full">
                        <Component className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Compliance accuracy gain</p>
                  </div>
                  
                  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Cost Savings</p>
                        <h4 className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                          {diagram?.benefits?.cost_savings || "40%"}
                        </h4>
                      </div>
                      <div className="p-2 bg-amber-100 dark:bg-amber-900/20 rounded-full">
                        <BarChart3 className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Operational cost reduction</p>
                  </div>
                  
                  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Risk Reduction</p>
                        <h4 className="text-2xl font-bold text-green-600 dark:text-green-400">
                          {diagram?.benefits?.risk_reduction || "60%"}
                        </h4>
                      </div>
                      <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-full">
                        <svg className="h-5 w-5 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Compliance risk mitigation</p>
                  </div>
                </div>
                
                {/* Analysis Content Card - Now below the metric cards */}
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-5">
                  <h3 className="text-lg font-semibold mb-4">Comprehensive Current State Analysis</h3>
                  <div className="prose dark:prose-invert max-w-none">
                    {text_html ? (
                      <div dangerouslySetInnerHTML={{ __html: text_html }} />
                    ) : text && typeof text === 'string' ? (
                      formatStructuredAnalysis(text)
                    ) : (
                      <p>No analysis was provided.</p>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {activeTab === "recommendations" && (
              <div className="p-6 space-y-6">
                <h2 className="text-xl font-bold pb-2 border-b flex items-center gap-2">
                  <LightbulbIcon className="h-5 w-5 text-purple-500" />
                  AI-Powered Recommendations
                </h2>
                
                <div className="space-y-4">
                  {advice_html ? (
                    <div className="prose dark:prose-invert max-w-none bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-5">
                      <div dangerouslySetInnerHTML={{ __html: advice_html }} />
                    </div>
                  ) : advice && typeof advice === 'string' ? (
                    <Accordion type="multiple" className="space-y-4">
                      {/* Parse the advice to find main recommendations */}
                      {findRecommendationSections(advice).map((section, index) => (
                        <AccordionItem 
                          key={index} 
                          value={`recommendation-${index}`}
                          className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                        >
                          <AccordionTrigger className="px-5 py-4 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/80">
                            <div className="flex flex-col items-start text-left">
                              <h3 className="text-lg font-semibold">{section.title}</h3>
                              {section.subtitle && (
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{section.subtitle}</p>
                              )}
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="px-5 py-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                            <div className="prose dark:prose-invert max-w-none">
                              {section.content}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  ) : (
                    <p>No specific recommendations provided.</p>
                  )}
                </div>
              </div>
            )}
            
            {activeTab === "comparison" && (
              <div className="p-6 space-y-6">
                <h2 className="text-xl font-bold pb-2 border-b flex items-center gap-2">
                  <GitCompareArrows className="h-5 w-5 text-purple-500" />
                  Process Comparison
                </h2>
                
                <div className="w-full overflow-auto">
                  <ProcessComparison diagram={diagram} />
                </div>
              </div>
            )}
            
            {activeTab === "timeline" && (
              <div className="p-6 space-y-6">
                <h2 className="text-xl font-bold pb-2 border-b flex items-center gap-2">
                  <Timer className="h-5 w-5 text-purple-500" />
                  Implementation Plan
                </h2>
                
                {implementationTimeline.length > 0 ? (
                  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-5">
                    <div className="space-y-8">
                      {implementationTimeline.map((phase, index) => (
                        <div key={index} className="relative pl-10 pb-8 border-l-2 border-purple-300 dark:border-purple-700 last:border-0">
                          {/* Phase dot */}
                          <div className="absolute left-[-9px] top-0 h-5 w-5 rounded-full bg-purple-500 dark:bg-purple-600" />
                          
                          {/* Phase content */}
                          <div className="bg-purple-50 dark:bg-purple-900/20 rounded-md p-4 shadow-sm border border-purple-100 dark:border-purple-800">
                            <div className="flex items-center mb-3">
                              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-800 text-purple-700 dark:text-purple-300 mr-2">
                                {index + 1}
                              </div>
                              <h4 className="font-medium text-lg">{phase.split(':')[0]}</h4>
                            </div>
                            <p className="text-gray-600 dark:text-gray-300">{phase.split(':')[1] || phase}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                    <Timer className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                    <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-1">No Implementation Timeline</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">No implementation timeline was provided in the response.</p>
                  </div>
                )}
                
                {/* Key metrics section */}
                {diagram?.key_metrics && diagram.key_metrics.length > 0 && (
                  <div className="mt-8">
                    <h3 className="text-xl font-medium mb-4">Key Performance Indicators</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {diagram.key_metrics.map((metric, index) => (
                        <div key={index} className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                          <div className="flex items-center mb-2">
                            <div className="w-2 h-8 bg-gradient-to-b from-purple-400 to-indigo-500 rounded-full mr-3"></div>
                            <span className="text-sm font-medium">{metric}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </Card>
      
      {/* Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Download PDF Report</DialogTitle>
            <DialogDescription>
              Enter your email address where we should send the PDF version of this report.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handlePdfRequest}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Input
                  id="email" 
                  className="col-span-4"
                  placeholder="your.email@example.com"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              
              {submitError && (
                <p className="text-red-500 text-sm">{submitError}</p>
              )}
            </div>
            
            <DialogFooter>
              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="bg-purple-600 hover:bg-purple-500"
              >
                {isSubmitting ? 'Sending...' : 'Send PDF'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Success Dialog */}
      <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Success!</DialogTitle>
            <DialogDescription>
              Your PDF report has been successfully requested.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex items-center justify-center py-6">
            <div className="rounded-full bg-green-100 p-3 dark:bg-green-900/30">
              <Check className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
          </div>
          
          <p className="text-center text-gray-600 dark:text-gray-300">
            The PDF will be sent to your email shortly. Please check your inbox.
          </p>
          
          <DialogFooter>
            <Button 
              onClick={() => setShowSuccessDialog(false)}
              className="bg-green-600 hover:bg-green-500 mt-2"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Helper function to parse recommendation sections for the accordion
function findRecommendationSections(text: string) {
  const sections = [];
  let currentSection = { title: '', subtitle: '', content: null as JSX.Element | null };
  
  // Try to find section headers (## Something)
  const lines = text.split('\n');
  let inSection = false;
  let sectionLines: string[] = [];
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    // Check if this is a main section header (## Something)
    if (line.match(/^##\s+.+$/)) {
      // If we were already in a section, add it to our results
      if (inSection && currentSection.title && sectionLines.length > 0) {
        sections.push({
          ...currentSection,
          content: formatRecommendationContent(sectionLines)
        });
      }
      
      // Start a new section
      const title = line.replace(/^##\s+/, '');
      currentSection = { 
        title: title, 
        subtitle: '', 
        content: null 
      };
      inSection = true;
      sectionLines = [];
      
      // Try to find a subtitle (the next non-blank line if it doesn't start with #)
      for (let j = i + 1; j < lines.length; j++) {
        const nextLine = lines[j].trim();
        if (nextLine && !nextLine.startsWith('#') && !nextLine.startsWith('-')) {
          currentSection.subtitle = nextLine.replace(/\*\*/g, '');
          break;
        }
        if (nextLine.startsWith('#')) break;
      }
    }
    // If we're in a section, collect its content
    else if (inSection) {
      sectionLines.push(line);
    }
  }
  
  // Add the last section if there is one
  if (inSection && currentSection.title && sectionLines.length > 0) {
    sections.push({
      ...currentSection,
      content: formatRecommendationContent(sectionLines)
    });
  }
  
  // If no sections were found with ## headers, try to use numbered sections instead
  if (sections.length === 0) {
    return [{
      title: "AI Recommendations",
      subtitle: "Detailed optimization suggestions",
      content: <div>{formatRecommendations(text)}</div>
    }];
  }
  
  return sections;
}

// Format the content of each recommendation section
function formatRecommendationContent(lines: string[]) {
  const content = lines.join('\n');
  return <div>{formatRecommendations(content)}</div>;
}

export default ComplianceAIResponse;
